import ChatGPTService from '@src/services/ChatGPTService';
import {
  fetchSpeakerFrameAnalysis,
  SpeakerIdentificationService,
} from '@src/services/speakers/SpeakerIdentificationService';
import { StoryScopedChatGPTService } from '@src/services/speakers/StoryScopedChatGPTService';
import { useDatoClient } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { useCallback, useMemo } from 'react';

import type { ScopedStory } from '@src/services/speakers/StoryScopedChatGPTService';
import type { TranscriptElement } from '@src/types.ts/transcript';

/** Speaker identification for the story open in the editor. */
export function useSpeakerIdentificationService(): SpeakerIdentificationService | null {
  const videoCreator = useVideoCreatorStore();
  const aiPromptRepository = useDatoClient()?.aiPromptRepository;
  return useMemo(
    () =>
      aiPromptRepository
        ? new SpeakerIdentificationService({
            identifier: new ChatGPTService(videoCreator, aiPromptRepository),
            analyzeFrames: fetchSpeakerFrameAnalysis,
          })
        : null,
    [videoCreator, aiPromptRepository],
  );
}

/**
 * Speaker identification for a story that need not be open — one fresh
 * story-scoped service per call (see `StoryScopedChatGPTService`).
 */
export function useStorySpeakerIdentificationFactory(): (
  story: ScopedStory,
  elements: TranscriptElement[],
) => SpeakerIdentificationService | null {
  const videoCreator = useVideoCreatorStore();
  const aiPromptRepository = useDatoClient()?.aiPromptRepository;
  return useCallback(
    (story, elements) =>
      aiPromptRepository
        ? new SpeakerIdentificationService({
            identifier: new StoryScopedChatGPTService(
              videoCreator,
              aiPromptRepository,
              story,
              elements,
            ),
            analyzeFrames: fetchSpeakerFrameAnalysis,
          })
        : null,
    [videoCreator, aiPromptRepository],
  );
}
